export type EnumType<T extends Record<string, unknown> = {}> = ({
    label: string;
    value: string;
} & T)[];

type EnumLabels<T extends Readonly<EnumType>> = T[number]["label"];
type EnumValues<T extends Readonly<EnumType>> = T[number]["value"];

export abstract class BaseEnum<const T extends Readonly<EnumType>> {
    public abstract select: T;

    public get labels(): EnumLabels<T>[] {
        return this.select.map((e) => e.label);
    }

    public get values(): EnumValues<T>[] {
        return this.select.map((e) => e.value);
    }

    protected get labelMap(): Map<EnumLabels<T>, EnumValues<T>> {
        return new Map(this.select.map((e) => [e.label, e.value]));
    }

    protected get valueMap(): Map<EnumValues<T>, EnumLabels<T>> {
        return new Map(this.select.map((e) => [e.value, e.label]));
    }

    public label(value: EnumValues<T>): EnumLabels<T> {
        return this.valueMap.get(value)!;
    }

    public value(label: EnumLabels<T>): EnumValues<T> {
        return this.labelMap.get(label)!;
    }

    public get Labels(): this["labels"][number] {
        throw new Error("Labels can't be used as a value");
    }

    public get Values(): this["values"][number] {
        throw new Error("Values can't be used as a value");
    }
}

export function classEnum<const T extends EnumType>(data: T) {
    const instance = class extends BaseEnum<T> {
        public override select = data;
    };

    return instance;
}

export function basicEnum<const T extends EnumType>(data: T) {
    return new (classEnum(data))();
}
